<svelte:options
  customElement={{
    shadow: 'open',
    props: {
      lang: { type: 'String' },
      referer: { type: 'String' },
      loginHref: { type: 'String' },
      theme: { type: 'String' },
      authServerUrl: { type: 'String' }
    }
  }}
/>

<script lang="ts">
  import { onMount } from 'svelte';
  import { getPassportOfferTranslations, type PassportOfferLanguage } from './translations';
  import PassportCheck from './images/PassportCheck.svelte';
  import StripeClimateBadge from './images/StripeClimateBadge.svelte';
  import StripeWordmark from './images/StripeWordmark.svelte';
  import PassportIcon from './images/teuteuf_passport.png';
  import GameStackPopover from './GameStackPopover.svelte';

  const INRIA_SANS_HREF =
    'https://fonts.googleapis.com/css2?family=Inria+Sans:wght@300;400;700&display=swap';
  const STYLESHEET_ID = 'teuteuf-passport-inria-sans';
  const PRECONNECT_ID = 'teuteuf-passport-gstatic-preconnect';

  function loadInriaSans() {
    if (typeof document === 'undefined') return;

    if (!document.getElementById(PRECONNECT_ID)) {
      const preconnect = document.createElement('link');
      preconnect.id = PRECONNECT_ID;
      preconnect.rel = 'preconnect';
      preconnect.href = 'https://fonts.gstatic.com';
      preconnect.crossOrigin = 'anonymous';
      document.head.appendChild(preconnect);
    }

    if (!document.getElementById(STYLESHEET_ID)) {
      const link = document.createElement('link');
      link.id = STYLESHEET_ID;
      link.rel = 'stylesheet';
      link.href = INRIA_SANS_HREF;
      document.head.appendChild(link);
    }
  }

  onMount(loadInriaSans);

  let {
    lang = 'en',
    referer,
    loginHref = '/login',
    authServerUrl,
    theme = 'auto'
  }: {
    /**
     * The language to use. Defaults to 'en'. Translations in translations.ts
     */
    lang?: PassportOfferLanguage | string;
    /**
     * The referer game name to send to the auth server. Defaults to 'worldle'.
     */
    referer?: string;
    /**
     * The URL to the login page on the current site. used for the "Already have a subscription?" link. Defaults to '/login'.
     */
    loginHref?: string;
    /**
     * The URL of the auth server to use for creating subscriptions. Defaults to 'https://auth.teuteuf.fr'.
     */
    authServerUrl?: string;
    /**
     * The theme to use. Defaults to 'auto'.
     */
    theme?: 'light' | 'dark' | 'auto';
  } = $props();

  let systemPrefersDark = $state(false);
  $effect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => (systemPrefersDark = mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  });

  const isDark = $derived(theme === 'dark' || (theme === 'auto' && systemPrefersDark));

  let selectedPlan = $state<'annual' | 'monthly'>('annual');
  let checkoutError = $state<'active_subscription' | 'generic' | ''>('');

  const t = $derived(getPassportOfferTranslations(lang));

  function getServerUrl() {
    return authServerUrl ?? 'https://auth.teuteuf.fr';
  }

  // ---- prices ----
  // Read from the auth server (the Stripe prices checkout uses). Until they
  // arrive, or if they can't be loaded, the fixed prices in the translations
  // are shown.

  type Price = { unitAmount: number; currency: string; interval: string; intervalCount: number };
  type Prices = { monthly: Price | null; yearly: Price | null };

  const PRICES_TIMEOUT_MS = 5000;
  // Currencies Stripe gives in whole units rather than hundredths
  const ZERO_DECIMAL_CURRENCIES = [
    'bif',
    'clp',
    'djf',
    'gnf',
    'jpy',
    'kmf',
    'krw',
    'mga',
    'pyg',
    'rwf',
    'ugx',
    'vnd',
    'vuv',
    'xaf',
    'xof',
    'xpf'
  ];

  let prices = $state<Prices | null>(null);

  onMount(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PRICES_TIMEOUT_MS);
    fetch(`${getServerUrl()}/stripe/passport/prices`, { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((data: Prices | null) => {
        if (data?.monthly && data?.yearly) prices = data;
      })
      .catch(() => {
        // Keep the fixed prices
      })
      .finally(() => clearTimeout(timer));
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  });

  // A Stripe amount (smallest unit) in normal units, e.g. 2999 USD -> 29.99
  const toMajor = (price: Price) =>
    ZERO_DECIMAL_CURRENCIES.includes(price.currency.toLowerCase())
      ? price.unitAmount
      : price.unitAmount / 100;

  function formatMoney(amount: number, currency: string): string | null {
    try {
      return new Intl.NumberFormat(lang || 'en', {
        style: 'currency',
        currency: currency.toUpperCase()
      }).format(amount);
    } catch {
      return null;
    }
  }

  // The price text to show instead of the fixed prices, or null to keep them
  const loadedPrices = $derived.by(() => {
    const monthly = prices?.monthly;
    const yearly = prices?.yearly;
    if (
      !monthly ||
      !yearly ||
      monthly.interval !== 'month' ||
      yearly.interval !== 'year' ||
      monthly.intervalCount !== 1 ||
      yearly.intervalCount !== 1 ||
      monthly.currency !== yearly.currency
    ) {
      return null;
    }
    const monthlyAmount = toMajor(monthly);
    const yearlyAmount = toMajor(yearly);
    const monthlyPrice = formatMoney(monthlyAmount, monthly.currency);
    const yearlyPrice = formatMoney(yearlyAmount, yearly.currency);
    const yearlyPerMonth = formatMoney(yearlyAmount / 12, yearly.currency);
    if (!monthlyPrice || !yearlyPrice || !yearlyPerMonth) {
      return null;
    }
    const savePercent = Math.round((1 - yearlyAmount / (monthlyAmount * 12)) * 100);
    return {
      monthlyPrice,
      annualMonthlyPrice: yearlyPerMonth,
      annualBillingDetail: t.annualBillingDetailTemplate.replace('{price}', yearlyPrice),
      // No badge if yearly isn't actually cheaper
      saveBadge:
        savePercent > 0 ? t.saveBadgeTemplate.replace('{percent}', String(savePercent)) : ''
    };
  });

  const shownPrices = $derived(
    loadedPrices ?? {
      monthlyPrice: t.monthlyPrice,
      annualMonthlyPrice: t.annualMonthlyPrice,
      annualBillingDetail: t.annualBillingDetail,
      saveBadge: t.saveBadge
    }
  );

  async function purchasePremium() {
    checkoutError = '';
    const plan = selectedPlan === 'annual' ? 'global_yearly' : 'global_monthly';

    const url = `${getServerUrl()}/stripe/create-subscription-v2/start?plan=${plan}&referer=${referer ?? 'worldle'}`;

    window.location.href = url;
  }
</script>

<main class="passport-offer" class:dark={isDark}>
  <section class="passport-offer__hero" aria-labelledby="passport-title">
    <div class="passport-offer__icon-wrap">
      <img src={PassportIcon} width="92" height="92" alt="" class="passport-offer__icon" />
    </div>
    <p class="passport-offer__eyebrow">{t.eyebrow}</p>
    <h1 id="passport-title">{t.title}</h1>
    <p class="passport-offer__story">{t.story}</p>
  </section>

  <section class="passport-offer__benefits" aria-labelledby="benefits-title">
    <div class="passport-offer__section-heading">
      <h2 id="benefits-title">{t.benefitsTitle}</h2>
    </div>
    <ul>
      <li>
        <PassportCheck />
        <span><strong>{t.benefitAdsTitle}</strong> {t.benefitAdsDetail}</span>
        <GameStackPopover {referer} label={t.gameListLabel} />
      </li>
      <li>
        <PassportCheck />
        <span><strong>{t.benefitSyncTitle}</strong> {t.benefitSyncDetail}</span>
      </li>
      <li>
        <PassportCheck />
        <span><strong>{t.benefitArchiveTitle}</strong> {t.benefitArchiveDetail}</span>
      </li>
      <li>
        <PassportCheck />
        <span><strong>{t.benefitEarlyAccessTitle}</strong> {t.benefitEarlyAccessDetail}</span>
      </li>
    </ul>
  </section>

  <section class="passport-offer__pricing" aria-labelledby="pricing-title">
    <div class="passport-offer__section-heading">
      <h2 id="pricing-title">{t.pricingTitle}</h2>
    </div>
    <div class="passport-offer__pricing-summary" aria-live="polite">
      <p class="passport-offer__selected-price">
        {#if selectedPlan === 'annual'}
          <span class="passport-offer__price-value">{shownPrices.annualMonthlyPrice}</span><span
            class="passport-offer__price-period">{t.annualMonthlyPeriod}</span
          >
        {:else}
          <span class="passport-offer__price-value">{shownPrices.monthlyPrice}</span><span
            class="passport-offer__price-period">{t.perMonth}</span
          >
        {/if}
      </p>
      <p class="passport-offer__selected-detail">
        {selectedPlan === 'annual' ? shownPrices.annualBillingDetail : t.monthlyBilling}
      </p>
    </div>
    <div class="passport-offer__plan-toggle" role="radiogroup" aria-label={t.pricingTitle}>
      <button
        class:selected={selectedPlan === 'annual'}
        class="passport-offer__toggle-option"
        type="button"
        role="radio"
        aria-checked={selectedPlan === 'annual'}
        onclick={() => (selectedPlan = 'annual')}
      >
        <span>{t.annualLabel}</span>
        {#if shownPrices.saveBadge}
          <span class="passport-offer__toggle-badge">{shownPrices.saveBadge}</span>
        {/if}
      </button>
      <button
        class:selected={selectedPlan === 'monthly'}
        class="passport-offer__toggle-option"
        type="button"
        role="radio"
        aria-checked={selectedPlan === 'monthly'}
        onclick={() => (selectedPlan = 'monthly')}
      >
        <span>{t.monthlyLabel}</span>
      </button>
    </div>
    <button class="passport-offer__cta" type="button" onclick={purchasePremium}>
      <span class="passport-offer__cta-label">
        {t.cta}
        <span class="passport-offer__cta-note">
          {t.finePrintBefore}
          <StripeWordmark />
        </span>
      </span>
      <span class="passport-offer__cta-arrow" aria-hidden="true">&#8594;</span>
    </button>
    {#if checkoutError}
      <p class="passport-offer__checkout-error" role="alert">
        {#if checkoutError === 'active_subscription'}
          <span>
            {t.checkoutActiveSubscription}
            <a href={loginHref}>{t.checkoutLogin}</a>.
            {t.checkoutManage}
            <a href="https://account.teuteuf.fr" target="_blank" rel="noopener noreferrer"
              >account.teuteuf.fr</a
            >.
          </span>
        {:else}
          {t.checkoutError}
        {/if}
      </p>
    {/if}
    <p class="passport-offer__supporting-copy">
      <span class="passport-offer__supporting-line">{t.supportingStudio}</span>
      <span class="passport-offer__supporting-line passport-offer__supporting-line--climate">
        <a
          class="passport-offer__climate-link"
          href="https://climate.stripe.com/F8OjEi"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t.supportingClimate}
          <StripeClimateBadge />
        </a>
      </span>
    </p>
  </section>
</main>

<style>
  :host {
    display: block;
    width: 100%;
  }

  .passport-offer {
    --passport-ink: #17233d;
    --passport-muted: #444d5c;
    --passport-faint: #6f7887;
    --passport-accent: #2563eb;
    --passport-accent-hover: #1d4ed8;
    --passport-accent-contrast: #f6fff5;
    --passport-paper: #f2f6ff;
    --passport-border: rgba(37, 99, 235, 0.16);
    --passport-section-border: rgba(37, 99, 235, 0.2);
    --passport-control-border: rgba(23, 35, 61, 0.16);
    --passport-control-surface: rgba(23, 35, 61, 0.08);
    --passport-selected-shadow: rgba(23, 35, 61, 0.14);
    --passport-focus-ring: rgba(37, 99, 235, 0.45);
    --passport-stack-more-ink: var(--passport-ink);
    --passport-stack-more-surface: rgba(255, 255, 255, 0.9);
    --passport-stack-more-border: rgba(23, 35, 61, 0.15);
    --passport-radius: 1.25rem;
    /* Keep the component's type scale adjustable from one place. */
    --passport-type-eyebrow: 1rem;
    --passport-type-title: clamp(1.65rem, 5vw, 2.2rem);
    --passport-type-body: 0.82rem;
    --passport-type-section: 1rem;
    --passport-type-detail: 0.8rem;
    --passport-type-price: 1.8rem;
    --passport-type-control: 0.85rem;
    --passport-type-caption: 0.78rem;
    --passport-type-cta: 1rem;
    --passport-type-micro: 0.7rem;
    container-type: inline-size;
    font-family:
      -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell',
      'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    width: 100%;
    box-sizing: border-box;
    margin-top: 0.5rem;
    padding: 0.75rem 1rem 0.8rem;
    color: var(--passport-ink);
    background:
      radial-gradient(circle at 90% 0%, rgba(191, 219, 254, 0.65), transparent 28%),
      var(--passport-paper);
    border: 1px solid var(--passport-border);
    border-radius: var(--passport-radius);
  }

  .passport-offer *,
  .passport-offer *::before,
  .passport-offer *::after {
    box-sizing: border-box;
  }

  .passport-offer.dark {
    --passport-ink: #f1f5ff;
    --passport-muted: #c7d1e3;
    --passport-faint: #8f9cb3;
    --passport-paper: #111a2a;
    --passport-border: rgba(147, 197, 253, 0.2);
    --passport-section-border: rgba(147, 197, 253, 0.3);
    --passport-control-border: rgba(241, 245, 255, 0.18);
    --passport-control-surface: rgba(241, 245, 255, 0.08);
    --passport-selected-shadow: rgba(0, 0, 0, 0.2);
    --passport-stack-more-ink: #111a2a;
    --passport-stack-more-surface: rgba(241, 245, 255, 0.9);
    --passport-stack-more-border: rgba(241, 245, 255, 0.3);
    background:
      radial-gradient(circle at 90% 0%, rgba(37, 99, 235, 0.2), transparent 30%),
      var(--passport-paper);
  }

  .passport-offer__hero {
    padding: 0.75rem 0 0.85rem;
    text-align: center;
  }

  .passport-offer__icon-wrap {
    display: grid;
    width: 4.25rem;
    height: 4.25rem;
    margin: 0 auto 0.55rem;
    place-items: center;
    border: 1px solid rgba(199, 164, 67, 0.42);
    border-radius: 1.2rem;
    background: linear-gradient(145deg, #fff4c7, #e6c45d);
    box-shadow: 0 0.75rem 1.75rem rgba(124, 94, 21, 0.18);
  }

  .passport-offer__icon {
    width: 3.4rem;
    height: 3.4rem;
    object-fit: contain;
    transform: rotate(8deg);
    filter: drop-shadow(0 0.25rem 0.25rem rgba(93, 66, 6, 0.2));
  }

  .passport-offer__eyebrow {
    margin: 0 0 0.3rem;
    color: var(--passport-accent);
    font-family: 'Inria Sans', sans-serif;
    font-size: var(--passport-type-eyebrow);
    font-weight: 700;
    letter-spacing: -0.03em;
  }

  .passport-offer h1 {
    max-width: 28rem;
    margin: 0 auto;
    font-family: 'Inria Sans', sans-serif;
    font-size: var(--passport-type-title);
    font-weight: 800;
    letter-spacing: -0.04em;
    line-height: 0.98;
  }

  .passport-offer__story {
    max-width: 31rem;
    margin: 0 auto 0;
    padding-top: 0.75rem;
    color: var(--passport-muted);
    font-size: var(--passport-type-body);
    line-height: 1.35;
  }

  .passport-offer__benefits {
    border-top: 1px solid var(--passport-section-border);
    padding: 0.7rem 0;
  }

  .passport-offer__section-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.45rem;
  }

  .passport-offer__section-heading h2 {
    margin: 0;
    font-size: var(--passport-type-section);
    font-weight: 800;
  }

  .passport-offer__benefits ul {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.45rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .passport-offer__benefits li {
    display: flex;
    align-items: flex-start;
    gap: 0.35rem;
    font-size: var(--passport-type-caption);
    line-height: 1.25;
  }

  .passport-offer__pricing {
    margin: 0;
    padding: 0.7rem 0 0;
    border-top: 1px solid var(--passport-section-border);
  }

  .passport-offer__pricing .passport-offer__section-heading {
    margin-bottom: 0.45rem;
  }

  .passport-offer__pricing .passport-offer__cta {
    margin-top: 0.5rem;
  }

  .passport-offer__pricing-summary {
    margin: 0 0 0.7rem;
    text-align: center;
  }

  .passport-offer__selected-price,
  .passport-offer__selected-detail {
    margin: 0;
  }

  .passport-offer__selected-price {
    font-size: var(--passport-type-price);
    font-weight: 800;
    letter-spacing: -0.04em;
    line-height: 1;
  }

  .passport-offer__selected-price .passport-offer__price-period {
    margin-left: 0.25rem;
    color: var(--passport-muted);
    font-size: var(--passport-type-micro);
    font-weight: 600;
    letter-spacing: 0;
  }

  .passport-offer__selected-detail {
    margin-top: 0.3rem;
    color: var(--passport-muted);
    font-size: var(--passport-type-detail);
  }

  .passport-offer__tax-note {
    margin: 0.2rem 0 0;
    color: var(--passport-faint);
    font-size: var(--passport-type-micro);
  }

  .passport-offer__plan-toggle {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.2rem;
    border: 1px solid var(--passport-control-border);
    border-radius: 999px;
    padding: 0.2rem;
    background: var(--passport-control-surface);
  }

  .passport-offer__toggle-option {
    display: flex;
    min-width: 0;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    border: 0;
    border-radius: 999px;
    padding: 0.5rem 0.65rem;
    color: var(--passport-muted);
    background: transparent;
    cursor: pointer;
    font: inherit;
    font-size: var(--passport-type-control);
    font-weight: 800;
    white-space: nowrap;
    transition:
      background 160ms ease,
      color 160ms ease,
      box-shadow 160ms ease;
  }

  .passport-offer__toggle-option.selected {
    color: var(--passport-ink);
    background: var(--passport-paper);
    box-shadow: 0 1px 4px var(--passport-selected-shadow);
  }

  .passport-offer__toggle-option:hover,
  .passport-offer__toggle-option:focus-visible {
    color: var(--passport-ink);
  }

  .passport-offer__toggle-option:focus-visible {
    outline: 3px solid var(--passport-focus-ring);
    outline-offset: 1px;
  }

  .passport-offer__toggle-badge {
    border-radius: 999px;
    padding: 0.15rem 0.35rem;
    color: #fff;
    background: rgba(37, 99, 235, 1);
    font-size: var(--passport-type-micro);
    font-weight: 800;
    letter-spacing: 0.02em;
  }

  .passport-offer__cta {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    border: 0;
    border-radius: 0.8rem;
    padding: 0.85rem;
    color: var(--passport-accent-contrast);
    background: var(--passport-accent);
    box-shadow: 0 0.45rem 0.9rem rgba(37, 99, 235, 0.25);
    cursor: pointer;
    text-align: left;
    transition:
      background 160ms ease,
      transform 160ms ease;
  }

  .passport-offer__cta-label {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.15rem;
  }

  .passport-offer__cta-label {
    font-size: var(--passport-type-cta);
    font-weight: 800;
  }

  .passport-offer__cta-note {
    color: rgba(246, 255, 245, 0.85);
    font-size: var(--passport-type-micro);
    font-weight: 500;
    line-height: 1.3;
  }

  .passport-offer__cta-arrow {
    flex: none;
    font-family:
      -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Segoe UI Symbol', 'Apple Symbols',
      'Noto Sans Symbols', Roboto, 'Helvetica Neue', Arial, sans-serif;
    font-size: var(--passport-type-cta);
    font-weight: 800;
  }

  .passport-offer__cta:hover {
    background: var(--passport-accent-hover);
    transform: translateY(-1px);
  }

  .passport-offer__cta:active {
    transform: translateY(0);
  }

  .passport-offer__cta:focus-visible {
    outline: 3px solid var(--passport-focus-ring);
    outline-offset: 3px;
  }

  .passport-offer__supporting-copy {
    display: grid;
    gap: 0.2rem;
    max-width: 32rem;
    margin: 0.6rem auto 0;
    color: var(--passport-ink);
    font-size: var(--passport-type-caption);
    font-weight: 500;
    line-height: 1.35;
    text-align: center;
  }

  .passport-offer__checkout-error {
    margin: 0.55rem auto 0;
    color: #b42318;
    font-size: var(--passport-type-caption);
    line-height: 1.35;
    text-align: center;
  }

  .passport-offer.dark .passport-offer__checkout-error {
    color: #ffb4ab;
  }

  .passport-offer__checkout-error a {
    color: inherit;
    font-weight: 700;
  }

  .passport-offer__supporting-line {
    display: block;
  }

  .passport-offer__climate-link {
    color: inherit;
    text-decoration: none;
  }

  .passport-offer__climate-link:hover,
  .passport-offer__climate-link:focus-visible {
    text-decoration: none;
  }

  .passport-offer__climate-link:focus-visible {
    outline: 3px solid var(--passport-focus-ring);
    outline-offset: 2px;
    border-radius: 0.25rem;
  }

  @media (max-width: 380px) {
    .passport-offer {
      padding-inline: 0.75rem;
    }
  }

  @media (min-width: 700px) {
    .passport-offer {
      --passport-type-body: 0.88rem;
      --passport-type-section: 1.15rem;
      --passport-type-caption: 0.9rem;
    }

    .passport-offer__story {
      line-height: 1.45;
    }

    .passport-offer__benefits li {
      line-height: 1.35;
    }
  }

  @media (max-width: 699px) and (max-height: 700px) {
    .passport-offer {
      padding: 0.5rem 0.75rem 0.55rem;
      --passport-type-title: clamp(1.6rem, 5vw, 2rem);
      --passport-type-body: 0.84rem;
      --passport-type-section: 1rem;
      --passport-type-detail: 0.8rem;
      --passport-type-price: 1.8rem;
      --passport-type-control: 0.85rem;
      --passport-type-caption: 0.8rem;
      --passport-type-cta: 1rem;
      --passport-type-micro: 0.7rem;
    }

    .passport-offer__hero {
      padding: 0.3rem 0 0.4rem;
    }

    .passport-offer__icon-wrap {
      width: 3.25rem;
      height: 3.25rem;
      margin-bottom: 0.35rem;
    }

    .passport-offer__icon {
      width: 2.6rem;
      height: 2.6rem;
    }

    .passport-offer__story {
      margin-top: 0.1rem;
      padding-top: 0.35rem;
      line-height: 1.25;
    }

    .passport-offer__benefits {
      padding: 0.45rem 0;
    }

    .passport-offer__section-heading {
      margin-bottom: 0.3rem;
    }

    .passport-offer__benefits li {
      line-height: 1.15;
    }

    .passport-offer__pricing {
      padding-top: 0.45rem;
    }

    .passport-offer__pricing-summary {
      margin-bottom: 0.45rem;
    }

    .passport-offer__selected-detail {
      margin-top: 0.2rem;
    }

    .passport-offer__toggle-option {
      padding-block: 0.4rem;
    }

    .passport-offer__cta {
      margin-top: 0.35rem;
      padding-block: 0.55rem;
    }

    .passport-offer__supporting-copy {
      margin-top: 0.35rem;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .passport-offer__cta,
    .passport-offer__toggle-option {
      transition: none;
    }
  }
</style>
